/**
 * Pure geometry helpers for the floor-plan renderer. No React, no DOM state —
 * everything here is unit-tested in geometry.test.ts.
 */

export interface Point {
  x: number;
  y: number;
}

/** Pan/zoom transform applied to the element group: screen = t + scale * world. */
export interface View {
  tx: number;
  ty: number;
  scale: number;
}

export const MIN_SCALE = 0.3;
export const MAX_SCALE = 4;

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Zoom by `factor` while keeping the floor point under (`vbX`,`vbY`) — given in
 * viewBox units — visually fixed. Scale is clamped to [MIN_SCALE, MAX_SCALE].
 */
export function zoomAt(view: View, vbX: number, vbY: number, factor: number): View {
  const scale = clamp(view.scale * factor, MIN_SCALE, MAX_SCALE);
  const applied = scale / view.scale;
  return {
    scale,
    tx: vbX - (vbX - view.tx) * applied,
    ty: vbY - (vbY - view.ty) * applied,
  };
}

/** Convert a viewBox-space point to world (pre-transform) coordinates. */
export function viewBoxToWorld(view: View, vbX: number, vbY: number): Point {
  return { x: (vbX - view.tx) / view.scale, y: (vbY - view.ty) / view.scale };
}

/** Rotate a point around the origin by `deg` degrees (SVG: +y is down, CW+). */
export function rotatePoint(x: number, y: number, deg: number): Point {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

/**
 * Map a world point into an element's local frame (origin at element center,
 * axis-aligned). Inverse of the element's translate+rotate transform.
 */
export function worldToLocal(
  world: Point,
  el: { cx: number; cy: number; rotation: number },
): Point {
  return rotatePoint(world.x - el.cx, world.y - el.cy, -el.rotation);
}

/** Angle in degrees from element center to a world point (0 = +x axis). */
export function angleFromCenter(el: { cx: number; cy: number }, world: Point): number {
  return (Math.atan2(world.y - el.cy, world.x - el.cx) * 180) / Math.PI;
}

/** Snap `deg` to the nearest `step` when within `tolerance` of it. */
export function snapAngle(deg: number, step = 15, tolerance = 4): number {
  const nearest = Math.round(deg / step) * step;
  return Math.abs(nearest - deg) <= tolerance ? nearest : deg;
}

/**
 * Seat-dot positions in element-local coordinates (relative to center,
 * unrotated). Circle tables ring the perimeter; rect tables seat two rows.
 */
export function seatPositions(
  shape: "circle" | "rect",
  seats: number,
  w: number,
  h: number,
): Point[] {
  if (seats <= 0) return [];
  const pts: Point[] = [];

  if (shape === "circle") {
    const r = (Math.min(w, h) / 2) * 0.82;
    for (let i = 0; i < seats; i++) {
      const a = (i / seats) * Math.PI * 2 - Math.PI / 2;
      pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    return pts;
  }

  const top = Math.ceil(seats / 2);
  const bottom = seats - top;
  const row = (count: number, y: number) => {
    for (let i = 0; i < count; i++) {
      const t = (i + 0.5) / count;
      pts.push({ x: (t - 0.5) * w * 0.82, y });
    }
  };
  row(top, (-h / 2) * 0.66);
  row(bottom, (h / 2) * 0.66);
  return pts;
}

/**
 * Symmetric resize about the element center: the pointer (in world space) sets
 * the new half-extent along the element's local axes. Keeps the center fixed,
 * which stays correct under rotation. Enforces a minimum size.
 */
export function resizeToPointer(
  el: { cx: number; cy: number; rotation: number },
  world: Point,
  min = 24,
): { w: number; h: number } {
  const local = worldToLocal(world, el);
  return {
    w: Math.max(min, Math.abs(local.x) * 2),
    h: Math.max(min, Math.abs(local.y) * 2),
  };
}
